import esriId from '@arcgis/core/identity/IdentityManager.js';
import React, { useEffect, useMemo, useState } from 'react';
import PratusLogoIcon from 'assets/images/pratus__logoIcon.svg?react';
import PratusLogoIconMono from 'assets/images/pratus__logoIcon--mono.svg?react';
import { NavLink, useNavigate, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateCurrentIncident,
  updateCurrentlySelectedGroup,
} from '../../actions/diceActions';
import { useChatSidebar } from '../GlobalChatSidebar/ChatSidebarProvider';
import { fetchExerciseWorkspaces } from '../../actions/workspaceActions';
import { getInitials } from '../../helpers/intials';
import SidebarNav from '../sidebarNav/SidebarNav';
import { useSidebarNavContext } from '../sidebarNav/SidebarNavProvider';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useBrowserAuth } from '../../providers/BrowserAuthProvider';
import {
  useStreamlinePlatformFlag,
  useHubChromeEnabled,
  useCfaFlag,
  useCnicFlag,
  useDhsFlag,
  useEquinorFlag,
  useEpaFlag,
  useGovernmentOfOntarioFlag,
  useJapanDemoFlag,
  useTennesseeNationalGuardFlag,
  useNotificationCenterFlag,
  useHwcgFlag,
} from 'utils/featureFlags';
import { displayGroupName } from 'utils/epaDemoOrgDisplay';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useIsDev } from '../../hooks/useIsDev';
import { PLATFORM_VERSION } from '../../environment';
import { useHealthCheck } from 'hooks/useHealthCheck';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import { useWebsocket } from '../../providers/WebsocketProvider';
import { PRATUS_KNOWLEDGE_URL } from 'assets/data/HelpContent';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useIncidentInfo } from '../IAP/hooks/incidentInfoHooks';
import { ToggleMapButton, OpPeriodWindowChips } from './PeriodButtonGroup';
import { useIncident } from 'components/IAP/hooks/incidentHooks';
import { useIsCommandCenter } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/isCommandCenter';
import { useOptionalUscgMapContext } from '../MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import { NotificationsBellMenu } from '../MapPage/CorporateViewTabs/NotificationsTab/NotificationsBellMenu';
import { EscalateResponseButton } from './EscalateResponseButton';

dayjs.extend(utc);

const PratusHeader: React.FC = () => {
  const { logout } = useBrowserAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isDev = useIsDev();
  const { data: healthCheck } = useHealthCheck(isDev);

  const { needsRefresh } = useWebsocket();

  const isCommandCenter = useIsCommandCenter();
  const [manualTooltipToggle, setManualTooltipToggle] = useState(false);
  const showTooltip = needsRefresh || manualTooltipToggle;

  const streamlinePlatform = useStreamlinePlatformFlag();
  const hubChrome = useHubChromeEnabled();
  const notificationCenter = useNotificationCenterFlag();
  const epaEnabled = useEpaFlag();
  const dhsEnabled = useDhsFlag();
  const cnicEnabled = useCnicFlag();
  const ontarioEnabled = useGovernmentOfOntarioFlag();
  const equinorEnabled = useEquinorFlag();
  const japanEnabled = useJapanDemoFlag();
  const cfaEnabled = useCfaFlag();
  const tngEnabled = useTennesseeNationalGuardFlag();
  const toggleRef = React.createRef<HTMLButtonElement>();

  const { toggle, isOpen, stuck } = useSidebarNavContext();
  const hwcg = useHwcgFlag();

  const user = useSelector((state: any) => {
    // TODO: obviously this should just be done in the reducer.
    // BEWARE using this user in dependency arrays. depend on a specific property.
    return {
      ...(state.app.user as User),
      ...(state.prepare.user as PrepareUser),
    };
  });

  const featureFlags = useSelector((state: any) => {
    return state.app.featureFlags as string[];
  });

  const currentGroup = useSelector((state: any) => {
    return state.app.currentlySelectedGroup as Group;
  });

  const groups = useSelector((state: any) => {
    return state.app.groups as Group[];
  });

  const incidents = useSelector((state: any) => {
    return state.app.incidents as IncidentAugmented[];
  });

  // Read the routed incident (same source the hamburger workspace selector and the IAP hooks use) so the
  // navbar title always matches the workspace the user is in — including AI-created incidents not yet in the
  // Redux incidents list (useIncident fetches by id). Avoids the header/hamburger divergence without adding a
  // competing writer to Redux `currentIncident`.
  const { data: currentIncident } = useIncident();
  // Escalation no longer advances the operational period, so the period number can't stand in for it: an escalated
  // incident stays on period 0 until its first Planning P cycle completes.
  const isEscalatedIncident =
    !isCommandCenter && !!currentIncident?.escalated_at;

  const { isVisible: isChatSidebarVisible, toggle: toggleChatSidebar } =
    useChatSidebar();

  const showIncidentInfo = location.pathname.startsWith('/iap');
  const isHub = location.pathname === '/hub';
  const showHighSecurityBanner = featureFlags.includes('HIGH_SECURITY');
  // Offline builds route Help at the bundled knowledge base instead of the
  // public docs site.
  const isOfflineEnvironment = featureFlags.includes('OFFLINE_ENVIRONMENT');
  const isOrgFiles = location.pathname.startsWith('/org_files');
  const isOrgSettings = location.pathname.startsWith('/groups');
  // Hub, incident workspace, and the exclusive org pages that can leave to the map.
  const showMapControls =
    showIncidentInfo || isCommandCenter || isOrgFiles || isOrgSettings;
  const { data: incidentInfo } = useIncidentInfo(showIncidentInfo);

  const mapCtx = useOptionalUscgMapContext();

  const groupOptions = useMemo(
    () =>
      groups
        .filter((group) => !group.is_closed)
        .map((group) => ({
          label: displayGroupName(group.group_name, {
            ontarioEnabled,
            equinorEnabled,
            japanEnabled,
            cnicEnabled,
            dhsEnabled,
            epaEnabled,
            cfaEnabled,
            tngEnabled,
          }),
          value: group.group_guid,
        })),
    [
      ontarioEnabled,
      equinorEnabled,
      japanEnabled,
      cnicEnabled,
      dhsEnabled,
      epaEnabled,
      cfaEnabled,
      tngEnabled,
      groups,
    ]
  );
  const currentGroupDisplayName = displayGroupName(currentGroup?.group_name, {
    ontarioEnabled,
    equinorEnabled,
    japanEnabled,
    cnicEnabled,
    dhsEnabled,
    epaEnabled,
    cfaEnabled,
    tngEnabled,
  });

  useEffect(() => {
    if (currentGroup.group_guid) {
      dispatch(
        fetchExerciseWorkspaces(user.user_guid, currentGroup.group_guid)
      );
    }
  }, [dispatch, user.user_guid, currentGroup.group_guid]);

  const organizationChangeHandler = (orgGuid: string) => {
    if (orgGuid !== currentGroup?.group_guid) {
      dispatch(
        updateCurrentlySelectedGroup(
          groups.find((group) => group.group_guid === orgGuid)
        )
      );
      const firstIncident = incidents.find(
        (incident) => incident.group_guid === orgGuid
      );
      dispatch(updateCurrentIncident(firstIncident));
      navigate(`/hub`);
    }
  };

  const handleLogout = async () => {
    esriId.destroyCredentials();
    await logout();
  };

  return (
    <>
      <header
        id="app-header"
        className={cn(
          'position-sticky zIndex--3 top-0',
          hubChrome && 'app-header--cfa dark tw:text-foreground'
        )}
      >
        {showHighSecurityBanner && (
          <div
            className="d-flex justify-content-center gap-1"
            style={{
              backgroundColor: 'var(--success-700)',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 4,
            }}
          >
            <span style={{ fontSize: '.7rem' }}>CUI Limited Pre-Release </span>
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip id="pre-release-tooltip">
                  PRATUS is in Initial Operating Capability. Full Operating
                  requirements will be defined based on your feature requests
                  and usage throughout the summer 2026 special events.
                </Tooltip>
              }
            >
              <span className="d-inline-flex align-items-center">
                <i
                  className="material-symbols-outlined"
                  style={{ fontSize: '.8rem' }}
                >
                  info
                </i>
              </span>
            </OverlayTrigger>
          </div>
        )}
        <div
          className={cn(
            'app-header-bar d-flex align-items-center',
            hubChrome && 'tw:text-foreground'
          )}
        >
          {/* Logo + hamburger — direct flex child, always protected */}
          <div
            className={cn(
              'd-flex logo-container align-items-center flex-shrink-0',
              // Matching gap and end margin read as equal spacing between the
              // logo, the menu glyph, and the title, because the negative
              // margin below cancels the icon button's optical padding.
              hubChrome ? 'tw:me-3 tw:gap-3' : 'me-3'
            )}
          >
            <PratusLogoIcon
              role="img"
              aria-label="Pratus"
              className="tw:ms-3 tw:h-7 tw:w-auto"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn(
                'tw:-mx-1.5',
                hubChrome ? 'tw:text-foreground' : 'tw:text-white'
              )}
              onClick={() => toggle()}
              ref={toggleRef}
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
            >
              <i className="material-symbols-outlined">
                {isOpen ? 'menu_open' : 'menu'}
              </i>
            </Button>
          </div>

          {/* Context title — fills remaining space between logo and right buttons.
              Overflow is limited to the text cluster so the notifications badge
              can sit slightly outside the button without being clipped. */}
          <div className="d-flex align-items-center header-incident-info flex-grow-1 gap-1">
            {showIncidentInfo && currentIncident ? (
              <>
                <div className="d-flex align-items-center min-w-0 flex-grow-1 gap-1">
                  <div className="d-flex align-items-center min-w-0 gap-1 overflow-hidden">
                    {currentIncident.name && (
                      <p
                        className={cn(
                          'text-truncate header-context-title mb-0',
                          hubChrome ? 'tw:text-foreground' : 'text-white'
                        )}
                      >
                        {currentIncident.name}
                      </p>
                    )}
                    {incidentInfo?.incidentLocation && (
                      <p
                        className={cn(
                          'header-incident-location d-none d-lg-block mb-0',
                          hubChrome ? 'tw:text-muted-foreground' : 'text-white'
                        )}
                      >
                        Location: {incidentInfo.incidentLocation}
                      </p>
                    )}
                  </div>
                  <OpPeriodWindowChips className="tw:hidden tw:shrink-0 tw:md:flex" />
                </div>
              </>
            ) : (
              isCommandCenter &&
              currentGroupDisplayName && (
                <p
                  className={cn(
                    'text-truncate header-context-title mb-0',
                    hubChrome ? 'tw:text-foreground' : 'text-white'
                  )}
                >
                  {currentGroupDisplayName}
                </p>
              )
            )}
          </div>

          {/* Right buttons — can shrink to zero and clip; avatar is protected separately */}
          <div className="d-flex align-items-center header-right-buttons gap-2">
            {notificationCenter && isCommandCenter ? (
              <NotificationsBellMenu />
            ) : null}
            {showMapControls && (
              <div className="d-none d-md-flex">
                <ToggleMapButton />
              </div>
            )}
            <div className="d-flex help-icon align-items-center gap-2 ps-2">
              <OverlayTrigger
                placement="bottom"
                show={showTooltip}
                overlay={
                  <Tooltip className="warning-tooltip">
                    <div className="tooltip-title">Reload Browser</div>
                    <div className="tooltip-body">
                      Changes have been made to the {currentGroup?.group_name}{' '}
                      group. Reload your browser to see updates.
                    </div>
                  </Tooltip>
                }
              >
                {hubChrome ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    style={{ display: needsRefresh ? 'block' : 'none' }}
                    onClick={() => setManualTooltipToggle(!manualTooltipToggle)}
                    aria-label="Reload browser warning"
                  >
                    <i className="material-symbols-outlined pulsing-warning-icon">
                      warning
                    </i>
                  </Button>
                ) : (
                  <StylishNewButton
                    style={{ display: needsRefresh ? 'block' : 'none' }}
                    className="button button--link"
                    onClick={() => setManualTooltipToggle(!manualTooltipToggle)}
                  >
                    <i className="material-symbols-outlined pulsing-warning-icon">
                      warning
                    </i>
                  </StylishNewButton>
                )}
              </OverlayTrigger>
              {showIncidentInfo && !isEscalatedIncident && (
                <EscalateResponseButton />
              )}
              {hubChrome ? (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="icon"
                        className="tw:text-foreground"
                        aria-label="Help"
                        title="Help"
                      />
                    }
                  >
                    <i className="material-symbols-outlined">help</i>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="dark tw:w-52">
                    {isHub ? (
                      <DropdownMenuItem
                        onClick={() => {
                          void import('components/MapPage/hubTour/hubTour')
                            .then(({ startHubTour }) => {
                              startHubTour();
                            })
                            .catch(() => {});
                        }}
                      >
                        Take a Hub tour
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuItem
                      onClick={() => {
                        if (isOfflineEnvironment) {
                          navigate('/knowledge_base');
                          return;
                        }
                        window.open(
                          PRATUS_KNOWLEDGE_URL,
                          '_blank',
                          'noopener,noreferrer'
                        );
                      }}
                    >
                      Knowledge base
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : isOfflineEnvironment ? (
                <NavLink to="/knowledge_base">
                  <i className="material-symbols-outlined color-primary-hover">
                    help
                  </i>
                </NavLink>
              ) : (
                <a
                  className="text-white"
                  href={PRATUS_KNOWLEDGE_URL}
                  target="_blank"
                  rel="noreferrer"
                >
                  <i className="material-symbols-outlined color-primary-hover">
                    help
                  </i>
                </a>
              )}
              {/* Unified PRATUS AI toggle (hub and non-hub chrome). The
                  non-hub header is not inside a `.dark` scope, so the button
                  carries it to pick up the dark shadcn palette. */}
              <Button
                type="button"
                size="sm"
                variant={isChatSidebarVisible ? 'default' : 'outline'}
                aria-pressed={isChatSidebarVisible}
                aria-label="PRATUS AI Chat"
                className={`${hubChrome ? '' : 'dark'}tw:ms-2 tw:hidden tw:md:inline-flex tw:text-nowrap`}
                onClick={toggleChatSidebar}
                title="PRATUS AI Chat"
                data-testid="pratus-ai-toggle"
              >
                PRATUS AI
              </Button>
              <Button
                type="button"
                size="sm"
                variant={isChatSidebarVisible ? 'default' : 'outline'}
                aria-pressed={isChatSidebarVisible}
                aria-label="PRATUS AI Chat"
                className={`${hubChrome ? '' : 'dark'}tw:ms-2 tw:inline-flex tw:gap-1 tw:md:hidden`}
                onClick={toggleChatSidebar}
                title="PRATUS AI Chat"
              >
                <PratusLogoIconMono style={{ height: '1em', width: 'auto' }} />
                AI
              </Button>
            </div>
          </div>

          <div className="flex-shrink-0 ps-2 pe-2">
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button
                    variant="ghost"
                    size="icon-lg"
                    className="tw:flex-shrink-0 tw:text-foreground"
                    aria-label="Profile menu"
                    title="Profile"
                  />
                }
              >
                <div className="avatar-initials sml">
                  {getInitials(
                    user.profile_settings?.userName ?? user.email_address
                  )}
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={4}
                className="dark tw:min-w-56"
                positionerClassName="tw:z-[1100]"
              >
                <div className="tw:flex tw:items-center tw:gap-2 tw:px-2 tw:py-1.5">
                  <div className="avatar-initials sml bg-bglight">
                    {getInitials(
                      user.profile_settings?.userName ?? user.email_address
                    )}
                  </div>
                  <div className="tw:flex tw:min-w-0 tw:flex-col">
                    <span className="tw:truncate tw:text-sm">
                      {user.profile_settings?.userName}
                    </span>
                    <span className="tw:truncate tw:text-xs tw:text-muted-foreground">
                      {user.email_address}
                    </span>
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      Sponsor Organization
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent
                      className="dark tw:min-w-56"
                      positionerClassName="tw:z-[1100]"
                    >
                      {groupOptions.length === 0 ? (
                        <DropdownMenuItem disabled>
                          No organizations
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuRadioGroup
                          value={currentGroup?.group_guid ?? ''}
                          onValueChange={organizationChangeHandler}
                        >
                          {groupOptions.map((option) => (
                            <DropdownMenuRadioItem
                              key={option.value}
                              value={option.value}
                            >
                              <span className="tw:truncate">
                                {option.label}
                              </span>
                            </DropdownMenuRadioItem>
                          ))}
                        </DropdownMenuRadioGroup>
                      )}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                </DropdownMenuGroup>
                {mapCtx ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuGroup>
                      <div className="tw:px-2 tw:py-1.5 tw:text-xs tw:text-muted-foreground">
                        Map
                      </div>
                      <DropdownMenuRadioGroup
                        value={mapCtx.mapViewMode}
                        onValueChange={(value) => {
                          if (value === '2d' || value === '3d') {
                            mapCtx.setMapViewMode(value);
                          }
                        }}
                      >
                        <DropdownMenuRadioItem value="2d">
                          2D map
                        </DropdownMenuRadioItem>
                      </DropdownMenuRadioGroup>
                    </DropdownMenuGroup>
                  </>
                ) : null}
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  <DropdownMenuItem render={<NavLink to="/profile" />}>
                    Profile
                  </DropdownMenuItem>
                  {streamlinePlatform ? null : (
                    <DropdownMenuItem render={<NavLink to="/activity" />}>
                      Activity Log
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    render={
                      <a
                        href={`${window.location.origin}/privacy.html`}
                        target="_blank"
                        rel="noreferrer"
                      />
                    }
                  >
                    Privacy Policy
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    render={
                      <a
                        href={`${window.location.origin}/tos.html`}
                        target="_blank"
                        rel="noreferrer"
                      />
                    }
                  >
                    Terms of Service
                  </DropdownMenuItem>
                  {isDev ? (
                    <>
                      <DropdownMenuItem
                        render={<NavLink to="/subscription_management" />}
                      >
                        Subscription Management
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        render={<NavLink to="/feature_flags" />}
                      >
                        Feature Flags
                      </DropdownMenuItem>
                      <DropdownMenuItem render={<NavLink to="/ai_usages" />}>
                        AI Usages
                      </DropdownMenuItem>
                      <div className="tw:px-2 tw:py-1 tw:text-center tw:text-xs tw:text-muted-foreground">
                        DICE: {PLATFORM_VERSION}
                      </div>
                      <div className="tw:px-2 tw:pb-1 tw:text-center tw:text-xs tw:text-muted-foreground">
                        {healthCheck?.status
                          ? `COMS: ${healthCheck.platform}`
                          : 'COMS: Not Healthy'}
                      </div>
                    </>
                  ) : null}
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => handleLogout()}>
                  <SharedIcon iconName="logout" />
                  Log Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <SidebarNav toggleRef={toggleRef} />
      {isOpen && !(hwcg && stuck) && (
        <div className="header-sidebar-overlay"></div>
      )}
    </>
  );
};

export default PratusHeader;
